import {
  Checkbox,
  MenuItem,
  TextFieldProps as MuiTextFieldProps,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import { Controller } from "react-hook-form";
import { isURL } from "validator";

type InputProps = {
  name: string;
  control: any;
  required?: boolean;
  startAdornment?: JSX.Element;
  endAdornment?: JSX.Element;
  selectOptions?: { label: string; value: string }[];
  multiple?: boolean;
  forLink?: boolean;
  removeMarginTop?: boolean;
  readOnly?: boolean;
  variant?: "outlined" | "filled" | "standard" | "pill";
  defaultSelectOption?: string;
};

type TextFieldProps = Omit<MuiTextFieldProps, "variant"> & {
  variant?: "outlined" | "filled" | "standard" | "pill";
};

const Input = ({
  name,
  control,
  label,
  required = false,
  autoComplete = "off",
  placeholder,
  variant = "outlined",
  multiline = false,
  fullWidth = false,
  select = false,
  multiple = false,
  selectOptions,
  sx,
  startAdornment,
  endAdornment,
  inputProps,
  forLink = false,
  removeMarginTop = false,
  defaultValue,
  readOnly = false,
  defaultSelectOption,
}: InputProps & TextFieldProps) => {
  const theme = useTheme();

  const formatLabel = (label: string) => {
    const parts = label.split(/(\(.*?\))/g);
    return parts.map((part, index) => (
      <span
        key={index}
        style={{
          color:
            part.startsWith("(") && part.endsWith(")")
              ? theme.colors.neutral.darkGray
              : "inherit",
        }}
      >
        {part}
      </span>
    ));
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: { value: required, message: `${label} is required` },
        validate:
          required && forLink
            ? (value) =>
                isURL(value, {
                  protocols: ["http", "https"],
                  require_protocol: true,
                }) || "Invalid URL"
            : undefined,
      }}
      render={({
        field: { onChange, value = defaultValue },
        fieldState: { error },
      }) => (
        <TextField
          helperText={error?.message}
          autoComplete={autoComplete}
          error={!!error}
          onChange={(event) => {
            const rawValue = event.target.value;
            let newValue: string | string[] =
              multiple && Array.isArray(rawValue)
                ? rawValue
                : (rawValue as string);

            if (multiline) {
              newValue = rawValue as string;
            } else if (
              multiple &&
              Array.isArray(newValue) &&
              defaultSelectOption
            ) {
              const hadDefault = value && value.includes(defaultSelectOption);
              const hasDefaultNow = newValue.includes(defaultSelectOption);

              if (hasDefaultNow && !hadDefault) {
                newValue = [defaultSelectOption];
              } else if (hadDefault) {
                newValue = newValue.length
                  ? newValue.filter((option) => option !== defaultSelectOption)
                  : [defaultSelectOption];
              } else if (!newValue.length) {
                newValue = [defaultSelectOption];
              }
            } else if (!multiline && typeof newValue === "string") {
              newValue = newValue.replace(/\n/g, "");
            }

            onChange(newValue);
          }}
          value={value}
          fullWidth={fullWidth}
          label={variant !== "pill" && `${label}${required ? "*" : ""}`}
          multiline={multiline}
          variant={variant === "pill" ? "outlined" : variant}
          inputProps={{
            ...inputProps,
            style: {
              whiteSpace: multiline ? "pre-wrap" : "normal",
            },
          }}
          placeholder={placeholder}
          InputProps={{
            readOnly: readOnly,
            startAdornment: startAdornment,
            endAdornment: endAdornment,
            style: {
              color: theme.colors.neutral.black,
              height: variant === "pill" ? 48 : undefined,
              paddingLeft: variant === "pill" ? 8 : undefined,
              backgroundColor:
                variant === "pill"
                  ? theme.colors.neutral.lightGray
                  : theme.colors.neutral.white,
              borderRadius: variant === "pill" ? 16 : undefined,
              borderColor: variant === "pill" ? "#EFF0F4B2" : undefined,
            },
          }}
          SelectProps={{
            multiple: multiple,
            renderValue: (selected: any) => {
              if (multiple) {
                return (
                  <Typography component="span">
                    {variant === "pill" && label && `${label}: `}
                    {selected
                      .map((value: string) => {
                        const option = selectOptions?.find(
                          (option) => option.value === value
                        );
                        return option ? formatLabel(option.label) : value;
                      })
                      .reduce((acc: any, curr: any) => (
                        <>
                          {acc}, {curr}
                        </>
                      ))}
                  </Typography>
                );
              } else {
                const option = selectOptions?.find(
                  (option) => option.value === selected
                );
                return option ? (
                  <Typography component="span">
                    {variant === "pill" && label && `${label}: `}
                    {formatLabel(option.label)}
                  </Typography>
                ) : (
                  selected
                );
              }
            },
            MenuProps: {
              sx: {
                "& .MuiPaper-root": {
                  boxShadow: `0px 6px 20px 0px ${theme.colors.action.hoverShadow}`,
                },
              },
              style: {
                maxHeight: 500,
              },
            },
          }}
          rows={multiline ? 6 : 1}
          select={select}
          sx={{
            ...sx,
            marginTop: removeMarginTop ? theme.spacing(0) : theme.spacing(3),
            marginBottom: theme.spacing(0),
            "& .MuiOutlinedInput-root":
              variant === "pill"
                ? {
                    borderRadius: 16,
                    "& fieldset": {
                      borderColor: theme.colors.neutral.lightGray,
                    },
                    "&:hover fieldset": {
                      borderColor: theme.colors.neutral.lightGray,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.colors.neutral.lightGray,
                    },
                  }
                : undefined,
          }}
        >
          {select &&
            selectOptions &&
            selectOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {multiple && (
                  <Checkbox checked={value.indexOf(option.value) > -1} />
                )}
                <Typography component="span">
                  {formatLabel(option.label)}
                </Typography>
              </MenuItem>
            ))}
        </TextField>
      )}
    />
  );
};

export default Input;
